import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../hooks/api.js';
import { useEditorStore, useEditorHistory } from '../stores/editorStore.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { EditorCanvas } from '../components/editor/EditorCanvas.js';
import { InspectorPanel } from '../components/InspectorPanel.js';
import { PanelResizer } from '../components/PanelResizer.js';
import { useResizablePanel } from '../hooks/use-resizable-panel.js';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts.js';
import { PaneLoading } from '../components/PaneLoading.js';
import type { Block, ComponentTarget } from '@blogforge/shared';

interface ComponentRow {
  id: string;
  name: string;
  description: string | null;
  target: ComponentTarget;
  version: number;
  blocks: string;
}

/**
 * Component designer — author a reusable component from scratch or by editing
 * an existing one. The canvas is the same block editor used for posts and
 * emails; saving blocks bumps the component version so documents that inserted
 * an older version can be offered a "push update".
 */
export function ComponentEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const inspector = useResizablePanel();

  // Keep the inspector overlay state in sync on small screens.
  useEffect(() => {
    document.body.classList.toggle('inspector-open', !inspector.collapsed);
    return () => document.body.classList.remove('inspector-open');
  }, [inspector.collapsed]);

  const { blocks, setBlocks, setComponentUsages, setPostId, markClean } = useEditorStore();
  const { undo, redo, canUndo, canRedo } = useEditorHistory();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState<ComponentTarget>('both');
  const [loadedMeta, setLoadedMeta] = useState<{
    name: string;
    description: string | null;
    target: ComponentTarget;
    version: number;
  } | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const savedFlashTimer = useRef<ReturnType<typeof setTimeout>>();
  const initialBlocksJson = useRef<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['component', id],
    queryFn: () => api.components.get(id!),
    enabled: !!id,
  });

  // Load an existing component into the editor.
  useEffect(() => {
    if (!id || !data) return;
    const row = data as ComponentRow;
    setPostId(`component-${row.id}`);
    setComponentUsages([]);
    let parsed: Block[] = [];
    try {
      parsed = JSON.parse(row.blocks) as Block[];
    } catch {
      parsed = [];
    }
    setBlocks(parsed);
    initialBlocksJson.current = JSON.stringify(parsed);
    setName(row.name);
    setDescription(row.description ?? '');
    setTarget(row.target);
    setLoadedMeta({
      name: row.name,
      description: row.description,
      target: row.target,
      version: row.version,
    });
  }, [data, id, setPostId, setComponentUsages, setBlocks]);

  // Fresh blank canvas for a new component.
  useEffect(() => {
    if (!isNew) return;
    setPostId('component-new');
    setComponentUsages([]);
    setBlocks([]);
    initialBlocksJson.current = '[]';
    setLoadedMeta(null);
    setName('');
    setDescription('');
    setTarget('both');
  }, [isNew, setPostId, setComponentUsages, setBlocks]);

  const blocksChanged =
    initialBlocksJson.current !== null && JSON.stringify(blocks) !== initialBlocksJson.current;
  const metaChanged =
    loadedMeta !== null &&
    (name !== loadedMeta.name ||
      description !== (loadedMeta.description ?? '') ||
      target !== loadedMeta.target);
  const dirty = isNew ? name.trim().length > 0 || blocks.length > 0 : blocksChanged || metaChanged;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const meta = {
        name: name.trim(),
        description: description.trim() || null,
        target,
      };
      if (isNew) {
        const created = (await api.components.create({
          ...meta,
          blocks,
        })) as { id: string };
        navigate(`/components/${created.id}/edit`, { replace: true });
        return;
      }
      await api.components.update(id!, {
        ...meta,
        // Only resend blocks when they actually changed — unchanged saves must
        // not bump the version (which would mark every usage as stale).
        ...(blocksChanged ? { blocks } : {}),
      });
    },
    onSuccess: () => {
      markClean();
      initialBlocksJson.current = JSON.stringify(blocks);
      setLoadedMeta({
        name: name.trim(),
        description: description.trim() || null,
        target,
        version: (loadedMeta?.version ?? 1) + (blocksChanged ? 1 : 0),
      });
      void qc.invalidateQueries({ queryKey: ['component', id] });
      void qc.invalidateQueries({ queryKey: ['components'] });
      setSavedFlash(true);
      clearTimeout(savedFlashTimer.current);
      savedFlashTimer.current = setTimeout(() => setSavedFlash(false), 2000);
    },
  });

  useEditorShortcuts({
    onSave: () => {
      if (name.trim() && !saveMutation.isPending) saveMutation.mutate();
    },
    undo,
    redo,
  });

  if (isLoading) {
    return <PaneLoading label="Loading component…" />;
  }

  return (
    <div
      className="editor-layout"
      style={{
        gridTemplateColumns: inspector.collapsed ? '1fr auto' : `1fr auto ${inspector.width}px`,
      }}
    >
      {/* ── Canvas side ─────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden">
        {/* Toolbar */}
        <EditorToolbar
          backTitle="components"
          onBack={() => navigate('/components')}
          onOpenInspector={() => inspector.setCollapsed(false)}
          title={{
            value: name,
            onChange: setName,
            onCommit: () => undefined,
            placeholder: 'Component name…',
          }}
          saveState={dirty ? 'dirty' : savedFlash ? 'saved' : 'idle'}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          onSave={() => saveMutation.mutate()}
          canSave={!!name.trim() && dirty}
          saving={saveMutation.isPending}
          primaryAction={
            <span className="chip shrink-0 px-2.5 py-1 font-mono text-[11px] tracking-tight normal-case">
              {loadedMeta ? `v${loadedMeta.version}` : 'new'}
            </span>
          }
        />

        {/* Component meta bar */}
        <div className="border-line bg-bg-soft/60 flex items-center gap-3 border-b px-4 py-2.5">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as ComponentTarget)}
            className="field-input w-auto py-1.5 text-xs"
            title="Where this component can be inserted"
          >
            <option value="post">Use in posts only</option>
            <option value="email">Use in emails only</option>
            <option value="both">Use in posts &amp; emails</option>
          </select>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description (optional)"
            className="field-input flex-1 py-1.5 text-xs"
          />
          <p className="border-line text-muted shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium">
            {blocks.length} block{blocks.length === 1 ? '' : 's'}
          </p>
        </div>

        {/* Canvas */}
        <EditorCanvas rawVariableDefinitions={undefined} />
      </div>

      {/* ── Block inspector (styles / per-block settings) ───── */}
      <PanelResizer
        collapsed={inspector.collapsed}
        onResizeStart={inspector.startResize}
        onExpand={() => inspector.setCollapsed(false)}
      />
      {!inspector.collapsed && (
        <>
          {/* Tap-out scrim — only when the inspector overlays the canvas (small screens) */}
          <div
            className="bg-ink/30 fixed inset-0 z-[75] backdrop-blur-[2px] lg:hidden"
            onClick={inspector.toggleCollapsed}
            aria-hidden="true"
          />
          <InspectorPanel
            post={undefined}
            postId={id ?? 'component'}
            mode="block"
            onToggleCollapse={inspector.toggleCollapsed}
          />
        </>
      )}
    </div>
  );
}
